/**
 * Employee Time Portal — the typed signature (P-TAS-20 Phase 3, App-2). The employee types
 * their name; it is drawn in a cursive font onto a canvas and sent as a PNG data URI in
 * Timesheet.Signature, which the timecard PDF draws as an image (API util/timecardSignature.ts
 * and the 2020PH / WCDP generators: a FIXED 300 × 150 box, so the PNG is 2:1 — 600 × 300 —
 * or it would be stretched). The background is transparent, like the mobile signature pad's
 * PNG, so it sits on the PDF's white page the same way.
 *
 * drawTypedSignature is the only part that touches a canvas, through a minimal interface,
 * so jest can drive it with a mock. renderTypedSignature adds the DOM: it waits for the
 * web font (document.fonts.load) before drawing — drawing first would bake in the fallback.
 */

export const SIGNATURE_FONT_FAMILY = 'Dancing Script';
export const SIGNATURE_FONT_WEIGHT = 600;
export const SIGNATURE_WIDTH = 600;
export const SIGNATURE_HEIGHT = 300;
const MAX_FONT_PX = 120;
const MIN_FONT_PX = 20;
const SIDE_PADDING = 24;
const INK = '#111111';

export interface SignatureContextLike {
  font: string;
  fillStyle: string | CanvasGradient | CanvasPattern;
  textAlign: CanvasTextAlign;
  textBaseline: CanvasTextBaseline;
  clearRect(x: number, y: number, w: number, h: number): void;
  measureText(text: string): { width: number };
  fillText(text: string, x: number, y: number): void;
}

export interface SignatureCanvasLike {
  width: number;
  height: number;
  getContext(kind: '2d'): SignatureContextLike | null;
  toDataURL(type?: string): string;
}

export function signatureFont(px: number, family: string = SIGNATURE_FONT_FAMILY): string {
  return `${SIGNATURE_FONT_WEIGHT} ${px}px "${family}", cursive`;
}

/**
 * Draws `name` centred on a transparent 600 × 300 canvas, shrinking the font until it fits
 * the width, and returns the PNG data URI. Throws when there is no 2D context or no name.
 */
export function drawTypedSignature(canvas: SignatureCanvasLike, name: string, family: string = SIGNATURE_FONT_FAMILY): string {
  const text = (name ?? '').trim().replace(/\s+/g, ' ');
  if (!text) throw new Error('A name is required to sign');
  canvas.width = SIGNATURE_WIDTH;
  canvas.height = SIGNATURE_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not available');
  ctx.clearRect(0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);
  let px = MAX_FONT_PX;
  ctx.font = signatureFont(px, family);
  while (px > MIN_FONT_PX && ctx.measureText(text).width > SIGNATURE_WIDTH - SIDE_PADDING * 2) {
    px -= 4;
    ctx.font = signatureFont(px, family);
  }
  ctx.fillStyle = INK;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  // Baseline in the lower part of the box: the PDF puts the image's bottom on the signature line.
  ctx.fillText(text, SIGNATURE_WIDTH / 2, Math.round(SIGNATURE_HEIGHT * 0.68));
  return canvas.toDataURL('image/png');
}

/** Waits for the signature web font (imported by myTimesheet.css). False when it can't load (offline): the cursive fallback is used. */
export async function loadSignatureFont(family: string = SIGNATURE_FONT_FAMILY): Promise<boolean> {
  try {
    if (typeof document === 'undefined' || !document.fonts) return false;
    const faces = await document.fonts.load(signatureFont(48, family));
    return faces.length > 0;
  } catch {
    return false;
  }
}

/** The PNG data URI for a typed name, drawn once the font is ready. */
export async function renderTypedSignature(name: string): Promise<string> {
  await loadSignatureFont();
  const canvas = document.createElement('canvas') as unknown as SignatureCanvasLike;
  return drawTypedSignature(canvas, name);
}
